import _ from 'lodash';
import moment from 'moment';
import React, { useEffect } from 'react';
import { useState } from 'react';
import { DatesRangeInput } from 'semantic-ui-calendar-react';
import type { StrictCheckboxProps } from 'semantic-ui-react';
import { Form, Icon, Loader, Message } from 'semantic-ui-react';
import useSWR from 'swr';
import type { Fetched, FoodOrder } from 'types';

import { useAuthContext } from '../../../../contexts/AuthContext';
import FoodFetcher from '../../../../data/FoodFetcher';
import { statusColors } from '../orders/constants';

import ExportInvoice from './export';
import InvoiceTable from './table';

/**
 * The invoice table's sort-and-filter state.
 *
 * `sortcolumn` is `string | ((order: FoodOrder) => unknown) | null` because it is
 * dispatched two ways: the table headers send a field-name string, while the SWR
 * `onSuccess` at line 67 sends an iteratee function that `_.sortBy` calls per row.
 * Both reach `_.sortBy` unchanged.
 */
type InvoiceSortState = {
  sortcolumn: string | ((order: FoodOrder) => unknown) | null;
  data?: FoodOrder[] | undefined;
  direction: 'ascending' | 'descending' | null;
};

/**
 * The actions this reducer accepts. `data` and `datevalue` ride along on the
 * actions that carry them rather than being split into separate variants,
 * because `CHANGE_SORT` is dispatched both with and without `data`.
 */
type InvoiceSortAction = {
  type: 'CHANGE_SORT' | 'FILTER' | 'CLEAR_FILTERS';
  sortcolumn?: InvoiceSortState['sortcolumn'] | undefined;
  data?: FoodOrder[] | undefined;
  datevalue?: string | undefined;
};

function Reducer(state: InvoiceSortState, action: InvoiceSortAction): InvoiceSortState {
  switch (action.type) {
    case 'CHANGE_SORT':
      if (state.sortcolumn === action.sortcolumn) {
        return {
          ...state,
          data: state.data!.slice().reverse(),
          direction: state.direction === 'ascending' ? 'descending' : 'ascending',
        };
      }
      return {
        sortcolumn: action.sortcolumn!,
        // `_.sortBy`'s overloads widen a `FoodOrder[] | undefined` input into a
        // union of the array's own methods; the result is the same rows in a new
        // order, which the assertion states.
        data: _.sortBy(state.data, [action.sortcolumn]) as FoodOrder[],
        direction: 'ascending',
      };
    case 'FILTER':
      return {
        ...state,
        data: action.data!.filter((d: FoodOrder) => {
          return moment(d.orderDate).local().format('YYYY-MM-DD') === moment(action.datevalue).format('YYYY-MM-DD');
        }),
      };
    case 'CLEAR_FILTERS':
      return {
        ...state,
        data: action.data,
        sortcolumn: null,
      };
    default:
      throw new Error();
  }
}

/**
 * What the invoices screen accepts. Two call sites in `../index.tsx` — the
 * roster-wide invoices tab (`patientId === null`) and the vendor-scoped render.
 * `selectedOrg` and `vendor` build the query string; `patientId` narrows it to
 * one member when present.
 *
 * `onPatientSelected` is forwarded by the caller but never read here — the
 * invoice table has no member drawer.
 */
type InvoicesProps = {
  /** Payer org, or the literal 'All' to span every org. */
  selectedOrg?: string | undefined;
  /** Vendor code the invoices are scoped to. */
  vendor?: string | undefined;
  /** Absent (or null) selects the roster-wide mode. */
  patientId?: string | null | undefined;
  /** Accepted but unread. */
  onPatientSelected?: ((patient: unknown) => void) | undefined;
};

function Invoices(props: InvoicesProps) {
  const authInfo = useAuthContext();
  const [selectedOrderIds, setselectedOrderIds] = useState<string[]>([]);
  const [selectedOrdersLength, setselectedOrdersLength] = useState(0);
  const [searchId, setSearchId] = useState('');
  const [dateState, setdateState] = useState('');
  const [startRange, setStartRange] = useState('');
  const [endRange, setEndRange] = useState('');
  const [loading, setLoading] = useState(true);
  const options = {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
    onSuccess: (data: Fetched<FoodOrder[]>) => {
      setLoading(false);
      dispatch({ type: 'CLEAR_FILTERS', data: data });
      dispatch({
        type: 'CHANGE_SORT',
        // Sorts by status band first, then date. `statusColors` is an open-key
        // map, so the lookup is `string | undefined` and unmatched statuses sort
        // together — existing behaviour, not a new fallback.
        sortcolumn: (od: FoodOrder) => {
          return [statusColors[od.status as string], od.orderDate];
        },
        data: data,
      });
    },
  };
  const url =
    `/order?vendorId=` +
    props.vendor +
    '&invoiceReady=1' +
    (props.patientId ? `&patientId=` + props.patientId : ``) +
    (searchId ? '&orderId=' + searchId : '') +
    (startRange
      ? '&createdDate:ge=' +
        moment(startRange).utc().format() +
        (endRange ? '&createdDate:le=' + moment(endRange).utc().add(1, 'days').format() : '')
      : '') +
    // (dateState? '&createdDate:ge='+moment(dateState.split(' -
    // ')[0]).utc().format('YYYY-MM-DD')+(dateState.split(' - ')[1]?
    // '&createdDate:le='+moment(dateState.split(' - ')[1]).utc().format('YYYY-MM-DD'):''):'')+
    (props.selectedOrg !== 'All' && !props.patientId ? '&patientOrg=' + props.selectedOrg : '');

  const { data: ordersData, error: ordersError } = useSWR<Fetched<FoodOrder[]>, Error>(
    [url, authInfo.token],
    FoodFetcher,
    options
  );

  const sortedOrdersData = ordersData?.sort((a: FoodOrder, b: FoodOrder) => {
    return new Date(b.orderDate!).getTime() - new Date(a.orderDate!).getTime();
  });
  useEffect(() => {
    dispatch({ type: 'CLEAR_FILTERS', data: sortedOrdersData });
    setselectedOrderIds([]);
    setselectedOrdersLength(0);
  }, [ordersData]);
  //Use sorted data instead of raw for sorting table values.
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- short-circuit used as a statement; the `if` that satisfies the rule is a different expression
  sortedOrdersData &&
    sortedOrdersData.map((order: FoodOrder) => {
      const quantity =
        order.orderItems &&
        order.orderItems.length > 0 &&
        _.sum(
          order.orderItems.map((od) => {
            return parseInt(od.quantity);
          })
        );
      // `quantity` is `number | false | undefined` off the `&&` chain, which is
      // wider than `FoodOrder.quantity`; the assertion records the existing
      // write rather than adding a coercion. The table's truthiness guard
      // already absorbs the `false`.
      order.quantity = quantity as number;
      //needs to be format as number with two decimal places. Cant use toFixed since it returns a string and throws off sorting dispatch
      /*
       * These `parseFloat` calls are handed a `number | string` where a `string`
       * is declared. A genuine disagreement, not a missing annotation: at
       * runtime the numeric arm is coerced via `toString`. Left standing as
       * TS2345s — an assertion would claim these are strings, and half are not.
       */
      order.shippingFees = order?.shippingFees ? Math.round(parseFloat(String(order.shippingFees)) * 100) / 100 : 0.0;
      order.foodPricing = order?.foodPricing ? Math.round(parseFloat(String(order.foodPricing)) * 100) / 100 : 0.0;
      order.foodPriceAndShipping =
        Object.prototype.hasOwnProperty.call(order, 'foodPricing') &&
        Object.prototype.hasOwnProperty.call(order, 'shippingFees') &&
        parseFloat(String(order.shippingFees)) + parseFloat(String(order.foodPricing));
    });
  /*
   * SUSPECTED DEFECT (this function) — REPORTED, NOT FIXED. `temp` aliases the
   * `selectedOrderIds` state array rather than copying it, so the `push` on the
   * checked branch mutates state in place. `setselectedOrderIds(temp)` is then
   * handed the same array reference React already holds, which it compares equal
   * and skips re-rendering for. The unchecked branch happens to work only
   * because `.filter` returns a fresh array. The row checkboxes therefore fail to
   * repaint on tick but do on untick. Typed as-is; `[...selectedOrderIds]` is the
   * fix and is a behaviour change.
   */
  const addExportData = (e: React.FormEvent<HTMLInputElement>, checkprops: StrictCheckboxProps, orderId?: string) => {
    let temp = selectedOrderIds;
    if (checkprops.checked) {
      temp.push(orderId!);
    } else {
      temp = temp.filter((o) => {
        return o !== orderId;
      });
    }
    setselectedOrderIds(temp);
    setselectedOrdersLength(temp.length);
  };
  const selectAll = (e: React.FormEvent<HTMLInputElement>, checkprops: StrictCheckboxProps) => {
    if (checkprops.checked) {
      // Both setters are handed `undefined` when `ordersData` has not arrived —
      // the `&&` short-circuits rather than guarding. Asserted to record that.
      setselectedOrderIds(
        (ordersData &&
          ordersData.map((d: FoodOrder) => {
            return d.orderId;
          })) as string[]
      );
      setselectedOrdersLength((ordersData && ordersData.length) as number);
    } else {
      setselectedOrderIds([]);
      setselectedOrdersLength(0);
    }
  };
  const handleSearchById = (e: React.MouseEvent<HTMLElement>, searchProps: { value?: string }) => {
    setSearchId(searchProps.value!);
  };
  const handleChangeDate = (event: React.SyntheticEvent<HTMLElement>, { name: _name, value }: { name?: string; value: string }) => {
    setdateState(value);
    // `split` always yields a first element, so `temp[0]` is present. `temp[1]`
    // is absent until the user picks the second date of the range, and the
    // assertion records that this code stores `undefined` into a `string` state
    // in that window rather than guarding it — the URL builder's truthiness
    // check on `endRange` is what already absorbs it.
    const temp = value.split(' - ');
    setStartRange(temp[0]!);
    setEndRange(temp[1]!);
  };

  const resetFilters = () => {
    setdateState('');
    setStartRange('');
    setEndRange('');
    setSearchId('');
    dispatch({ type: 'CLEAR_FILTERS', data: sortedOrdersData });
  };

  const [state, dispatch] = React.useReducer(Reducer, {
    sortcolumn: null,
    data: sortedOrdersData && sortedOrdersData,
    direction: null,
  });
  /*
   * `column` is destructured but the reducer never writes a `column` key — it
   * writes `sortcolumn`. So `column` is permanently `undefined` and every
   * `props.column === '<field>'` test in the table below is permanently false:
   * no header ever shows its sort arrow, even though the sorting itself works.
   * SUSPECTED DEFECT, reported not fixed — renaming this to `sortcolumn` would
   * start rendering arrows that do not render today.
   *
   * Typed through `InvoiceSortState & { column?: … }` rather than by widening the
   * state type, so the absent key stays visible as absent.
   */
  const { column, data, direction } = state as InvoiceSortState & { column?: string | null };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <div style={{ float: 'left' }}>
          <Form autoComplete="off">
            <Form.Group>
              <Form.Input
                placeholder="Search by ID"
                icon={<Icon name="search" onClick={handleSearchById} />}
                value={searchId}
                onChange={(e, searchProps) => setSearchId(searchProps.value)}
                name="searchId"
              />
              <DatesRangeInput
                name="datesRange"
                placeholder="Filter by Date Range"
                value={dateState}
                iconPosition="left"
                dateFormat="YYYY-MM-DD"
                onChange={handleChangeDate}
              />
              <Form.Button content="Clear" color="blue" onClick={resetFilters} />
            </Form.Group>
          </Form>
        </div>
        <div style={{ float: 'right' }}>
          {selectedOrderIds && selectedOrderIds.length > 0 && (
            <ExportInvoice
              ordersData={ordersData && ordersData}
              selectedOrderIds={selectedOrderIds}
              selectedOrdersLength={selectedOrdersLength}
              vendor={props.vendor}
            />
          )}
        </div>
      </div>
      {((!ordersData && !ordersError) || loading) && <Loader active inline="centered" />}
      {ordersError && <Message header="Oops!. Something went wrong.." content={ordersError.message} negative />}
      {data && data.length > 0 && !ordersError && (
        <InvoiceTable
          column={column}
          direction={direction}
          dispatch={dispatch}
          selectedOrderIds={selectedOrderIds}
          orders={data}
          addExportData={addExportData}
          selectAll={selectAll}
        />
      )}
      {ordersData && ordersData.length == 0 && <Message header="Info" content="0 Items found!" warning />}
    </>
  );
}

export default Invoices;
